// EL EMISOR DE LA RESERVA: un solo tramo que agenda de verdad (D22-4).
//
// Es el MEDIO de la rama `schedule` de `compile.ts`, tal cual, sacado a su
// propia casa: buscar disponibilidad → ofrecer el horario exacto → listar
// alternativas y elegir → reservar → decidir por el estado de la reserva, más
// las DOS salidas por las que el tramo se rinde (la especialidad que no se
// ofrece, y la agenda sin un solo hueco).
//
// POR QUÉ EXISTE. La etapa `agenda` de un proceso compila exactamente este
// tramo, y un segundo emisor suyo sería la misma conversación escrita dos
// veces: dos copias de las mismas aristas, de la misma toma de datos y de la
// misma promesa de F6-5, derivando la una de la otra desde el primer arreglo.
// Hoy tiene UN solo llamador — la fábrica de categorías —, y el siguiente es la
// captura por etapas, que pasa otros ids, otros argumentos y otros textos.
//
// LO QUE EL LLAMADOR PONE. El tramo no inventa un solo id ni un solo texto: los
// ids llegan enteros en `ids` (sin derivación ni prefijos), los textos en
// `copy`, la toma de datos de la visita y los mensajes de confirmación llegan
// como bloques ya emitidos que el tramo INTERCALA donde van. Así el orden de
// los arreglos — que es lo que la herramienta de deriva recorre, no el grafo —
// queda idéntico al que la fábrica escribía a mano.
//
// PURE + web-safe: no React, no DB, no node builtins.

import type { StarterEdge, StarterFlow, StarterNode } from './flow.js'

/** Cada id que este tramo emite, EXPLÍCITO. No hay derivación aquí a propósito:
 *  la fábrica pasa los nombres de siempre (`buscar_cita`, `hay_cita`, …) y la
 *  identidad byte por byte de lo compilado es lo que lo prueba. */
export interface BookingSegmentIds {
  buscar: string
  hay: string
  exacto: string
  ofrecer: string
  decidirDirecto: string
  elegirDirecto: string
  aviso: string
  listar: string
  elegirLista: string
  reservar: string
  confirmar: string
  ocupado: string
  leadNoofrece: string
  noofrece: string
  finNoofrece: string
  leadSin: string
  sin: string
  finSin: string
}

/** Los textos que ESTE tramo dice. Vienen ya resueltos (`CompilerCopy` +
 *  `DEFAULT_COPY` los resuelve el compilador); aquí no viven constantes. */
export interface BookingSegmentCopy {
  /** Se ofrece el horario exacto que el cliente pidió (sí/no). */
  ofrecerExacto: string
  /** No estaba esa hora: se avisa antes de listar las cercanas. */
  avisoAlternativas: string
  /** La lista de horarios y la pregunta por número. */
  listarOpciones: string
  /** El horario se ocupó entre el ofrecimiento y la reserva. */
  horarioOcupado: string
  /** La especialidad pedida no se ofrece (distinto de "no hay lugar"). */
  noOfrece: string
  /** Se ofrece, pero no queda un solo hueco. */
  sinCupo: string
}

/** EL ESTADO CON EL QUE UNA RESERVA QUEDA EN FIRME. Es el valor que
 *  `book_appointment` devuelve en `status` cuando la agendó de verdad
 *  ('scheduled' es apartada y 'unavailable' es la carrera perdida). Vive aquí,
 *  con el tramo que decide por él, porque desde D22-6 hay DOS ramas que lo
 *  comparan — la del tramo y la del cierre de la captura por etapas — y una
 *  cadena escrita dos veces se separa al primer cambio de vocabulario. */
export const BOOKING_CONFIRMED_STATUS = 'confirmed'

/** LOS TEXTOS DEL TRAMO CUANDO NADIE CONFIGURÓ LOS SUYOS, palabra por palabra
 *  los de hoy.
 *
 *  Viven aquí, con el tramo que los dice, porque desde D22-6 lo arman DOS
 *  emisores — la categoría `cita` de los presets de giro (a través de
 *  `DEFAULT_COPY` de compile.ts, que toma estas mismas cadenas) y la etapa
 *  `agenda` de un proceso (capture-flow.ts) — y dos copias de la misma frase
 *  serían el mismo tramo diciendo cosas distintas según quién lo emitió.
 *
 *  `noOfrece` y `sinCupo` NO son las del arranque de salud: aquéllas son
 *  CONFIGURACIÓN de esa categoría (`action.notOffered` / `action.noSlots`, que
 *  el cliente edita), y éstas son lo que dice un proceso que no configuró nada. */
export const DEFAULT_BOOKING_COPY: BookingSegmentCopy = {
  ofrecerExacto:
    'Tengo disponible ${disponibilidad.slots[0].starts_at_label} con ${disponibilidad.slots[0].doctor_name}. ¿Te la agendo? Responde sí o no.',
  avisoAlternativas:
    'No tengo exactamente esa hora, pero te comparto los horarios disponibles más cercanos.',
  listarOpciones:
    'Estos son los horarios disponibles:\n${disponibilidad.options_label}\n\n¿Cuál te queda mejor? Responde el número.',
  horarioOcupado: 'Uy, ese horario se acaba de ocupar. Déjame buscarte otra opción.',
  noOfrece:
    'Por ahora no tengo esa opción en la agenda. Te paso con una persona del equipo para revisarlo.',
  sinCupo:
    'Por ahora no tengo horarios disponibles. Te paso con una persona del equipo para encontrarte un espacio.',
}

/** La TOMA DE DATOS de la visita: la cadena de preguntas que el llamador hace
 *  entre elegir el horario y reservar. Llega ya emitida porque sus preguntas
 *  son del giro (motivo, primera visita, cobertura) y no del tramo; el tramo
 *  sólo la intercala y la encadena. */
export interface BookingSegmentIntake {
  nodes: StarterNode[]
  edges: StarterEdge[]
  /** A dónde llegan los dos caminos que eligen horario. */
  entryId: string
  /** El último nodo de la cadena: de ahí se pasa a reservar. */
  exitId: string
}

/** LOS MENSAJES DE LA RESERVA HECHA, también del llamador: la cita en firme y
 *  la apartada. El tramo decide por el estado y entra a ellos. */
export interface BookingSegmentBooked {
  nodes: StarterNode[]
  edges: StarterEdge[]
  /** `reserva.status == "confirmed"`. */
  confirmedId: string
  /** Cualquier otro estado: quedó apartada. */
  pendingId: string
}

/** La cuadrícula del carril donde este tramo se dibuja. `firstRow` es el
 *  renglón donde el tramo empieza, para que el llamador pueda dejar sus propias
 *  preguntas de entrada arriba. */
export interface BookingSegmentLayout {
  x: number
  y: number
  col: number
  row: number
  firstRow?: number
}

export interface BookingSegmentOptions {
  ids: BookingSegmentIds
  /** La expresión del argumento `specialty` de la consulta de disponibilidad. */
  specialtyExpr: string
  /** La expresión del argumento `when` de la consulta de disponibilidad. */
  whenExpr: string
  /** Los argumentos de `book_appointment` MÁS ALLÁ del horario elegido, que es
   *  lo único que el tramo sabe de suyo (`slot_id` sale de su propio `elegido`). */
  bookArgs: Record<string, unknown>
  /** Los argumentos de `capturar_lead` de las dos salidas por las que el tramo
   *  se rinde. */
  leadArgs: Record<string, unknown>
  copy: BookingSegmentCopy
  layout: BookingSegmentLayout
  intake: BookingSegmentIntake
  booked: BookingSegmentBooked
}

export interface BookingSegment {
  nodes: StarterNode[]
  edges: StarterEdge[]
  /** Por dónde entra la conversación al tramo: la consulta de disponibilidad. */
  entryId: string
}

/** Emite el tramo de reserva. Devuelve nodos y aristas en el ORDEN en el que la
 *  fábrica los escribía, que es el orden que los arreglos compilados llevan. */
export function buildBookingSegment(opts: BookingSegmentOptions): BookingSegment {
  const { ids, copy, intake, booked, layout } = opts
  const firstRow = layout.firstRow ?? 0
  const gx = (col: number): number => layout.x + col * layout.col
  const gy = (row: number): number => layout.y + (firstRow + row) * layout.row

  const nodes: StarterNode[] = [
    {
      id: ids.buscar,
      type: 'act',
      position: { x: gx(0), y: gy(0) },
      config: {
        tool: 'lookup_availability',
        args: { specialty: opts.specialtyExpr, when: opts.whenExpr },
        out: 'disponibilidad',
      },
    },
    { id: ids.hay, type: 'dec', position: { x: gx(0), y: gy(1) }, config: {} },
    { id: ids.exacto, type: 'dec', position: { x: gx(0), y: gy(2) }, config: {} },
    {
      id: ids.ofrecer,
      type: 'ask',
      position: { x: gx(0), y: gy(3) },
      config: {
        prompt: copy.ofrecerExacto,
        var: 'confirmar_directo',
        extract: { kind: 'boolean' },
        max_retries: 2,
      },
    },
    { id: ids.decidirDirecto, type: 'dec', position: { x: gx(0), y: gy(4) }, config: {} },
    {
      id: ids.elegirDirecto,
      type: 'act',
      position: { x: gx(0), y: gy(5) },
      config: {
        tool: 'select_slot',
        args: { slots: '${disponibilidad.slots}', choice: 1 },
        out: 'elegido',
      },
    },
    {
      id: ids.aviso,
      type: 'msg',
      position: { x: gx(1), y: gy(3) },
      config: {
        text: copy.avisoAlternativas,
      },
    },
    {
      id: ids.listar,
      type: 'ask',
      position: { x: gx(1), y: gy(4) },
      config: {
        prompt: copy.listarOpciones,
        var: 'opcion',
        extract: { kind: 'regex', pattern: '([1-9])' },
        max_retries: 2,
      },
    },
    {
      id: ids.elegirLista,
      type: 'act',
      position: { x: gx(1), y: gy(5) },
      config: {
        tool: 'select_slot',
        args: { slots: '${disponibilidad.slots}', choice: '${opcion}' },
        out: 'elegido',
      },
    },
    // La toma de datos del llamador va AQUÍ, entre elegir horario y reservar.
    ...intake.nodes,
    {
      id: ids.reservar,
      type: 'act',
      position: { x: gx(0), y: gy(7) },
      config: {
        tool: 'book_appointment',
        args: { slot_id: '${elegido.id}', ...opts.bookArgs },
        out: 'reserva',
      },
    },
    { id: ids.confirmar, type: 'dec', position: { x: gx(0), y: gy(8) }, config: {} },
    // Los mensajes de la reserva hecha, también del llamador.
    ...booked.nodes,
    {
      id: ids.ocupado,
      type: 'msg',
      position: { x: gx(2), y: gy(8) },
      config: {
        text: copy.horarioOcupado,
      },
    },
    // F6-5 — LO QUE SE PROMETE, SE CUMPLE, Y EL PROSPECTO NO SE EVAPORA.
    //
    // Las dos salidas por las que este tramo se rinde (la especialidad que la
    // clínica no ofrece, y la agenda sin un solo hueco) prometen las dos un
    // coordinador. Antes iban al nodo `pasar`, y el runtime pausa el turno
    // DESPUÉS de un `msg` cuyo siguiente nodo no es `ask`/`fin`/`route`
    // (run-skill, §4.1): la promesa salía y el traspaso se quedaba esperando al
    // turno siguiente, que ya era otra conversación. Ahora cada salida termina
    // en un `fin.handoff` propio, que el `msg` sí alcanza en el MISMO turno, con
    // una razón que dice al equipo por qué entra.
    //
    // Y antes de prometer, se guarda: quien pidió una cita y dio una fecha es un
    // prospecto caliente aunque la cita no se haya podido agendar. El
    // `capturar_lead` va DELANTE del mensaje (un `act` no pausa).
    {
      id: ids.leadNoofrece,
      type: 'act',
      position: { x: gx(2), y: gy(0) },
      config: { tool: 'capturar_lead', args: opts.leadArgs, out: 'lead_cita' },
    },
    {
      id: ids.noofrece,
      type: 'msg',
      position: { x: gx(2), y: gy(1) },
      config: { text: copy.noOfrece },
    },
    {
      id: ids.finNoofrece,
      type: 'fin',
      position: { x: gx(2), y: gy(2) },
      config: { kind: 'handoff', reason: 'cita_especialidad_no_ofrecida' },
    },
    {
      id: ids.leadSin,
      type: 'act',
      position: { x: gx(2), y: gy(3) },
      config: { tool: 'capturar_lead', args: opts.leadArgs, out: 'lead_cita' },
    },
    {
      id: ids.sin,
      type: 'msg',
      position: { x: gx(2), y: gy(4) },
      config: { text: copy.sinCupo },
    },
    {
      id: ids.finSin,
      type: 'fin',
      position: { x: gx(2), y: gy(5) },
      config: { kind: 'handoff', reason: 'cita_sin_disponibilidad' },
    },
  ]

  const edges: StarterEdge[] = [
    { from: ids.buscar, to: ids.hay },
    // Specialty not offered by the clinic → say so (distinct from no slots).
    { from: ids.hay, to: ids.leadNoofrece, when: 'disponibilidad.specialty_offered == false' },
    // Offered but no free slots at all → coordinator.
    { from: ids.hay, to: ids.leadSin, when: 'disponibilidad.slots.length == 0' },
    { from: ids.hay, to: ids.exacto, when: 'true' },
    // Is the requested time itself free?
    { from: ids.exacto, to: ids.ofrecer, when: 'disponibilidad.requested_available == true' },
    { from: ids.exacto, to: ids.aviso, when: 'true' },
    // Offer the exact match; "sí" books it, "no" lists alternatives.
    { from: ids.ofrecer, to: ids.decidirDirecto },
    { from: ids.decidirDirecto, to: ids.elegirDirecto, when: 'confirmar_directo == true' },
    { from: ids.decidirDirecto, to: ids.listar, when: 'true' },
    // Both slot-selection paths flow through intake (motivo → primera vez)
    // before the cita is booked.
    { from: ids.elegirDirecto, to: intake.entryId },
    // Alternatives path: aviso → list → pick → select.
    { from: ids.aviso, to: ids.listar },
    { from: ids.listar, to: ids.elegirLista },
    { from: ids.elegirLista, to: intake.entryId },
    ...intake.edges,
    // Una toma de datos VACÍA — `entryId` y `exitId` puestos los dos en
    // `ids.reservar`, que es lo que pasa una captura por etapas: sus preguntas
    // ya se hicieron ANTES del tramo — entra directo a reservar. Sin esta
    // guarda saldría la arista `reservar → reservar`, un lazo que el runtime
    // seguiría para siempre.
    ...(intake.exitId === ids.reservar ? [] : [{ from: intake.exitId, to: ids.reservar }]),
    // Book, then confirm (held-pending vs auto-confirmed vs lost-the-race).
    { from: ids.reservar, to: ids.confirmar },
    { from: ids.confirmar, to: ids.ocupado, when: 'reserva.status == "unavailable"' },
    {
      from: ids.confirmar,
      to: booked.confirmedId,
      when: `reserva.status == "${BOOKING_CONFIRMED_STATUS}"`,
    },
    { from: ids.confirmar, to: booked.pendingId, when: 'true' },
    ...booked.edges,
    // Slot taken between offer and book → apologise and search again.
    { from: ids.ocupado, to: ids.buscar },
    // Las dos salidas de rendición: guardar el prospecto, decirlo, traspasar DE
    // VERDAD en este mismo turno (el `fin` es de los tres tipos de nodo que un
    // `msg` sí alcanza sin pausar).
    { from: ids.leadNoofrece, to: ids.noofrece },
    { from: ids.noofrece, to: ids.finNoofrece },
    { from: ids.leadSin, to: ids.sin },
    { from: ids.sin, to: ids.finSin },
  ]

  return { nodes, edges, entryId: ids.buscar }
}

/** Toda arista de un flujo emitido tiene que nombrar nodos que EXISTEN.
 *
 *  Una arista colgando no truena al compilar: truena en la conversación, turnos
 *  después, cuando el runtime busca el nodo destino y escala con `invalid_state`
 *  delante del cliente. Por eso esto LANZA y no avisa: una arista así es un
 *  error del compilador, y el lugar donde se paga es aquí.
 *
 *  No hay destinos centinela que excluir: cada salida compartida (`pasar`,
 *  `cierre`, `fin_ok`, `responder`, `discover`) es un nodo emitido del flujo. */
export class DanglingEdgeError extends Error {}

export function assertNoDanglingEdges(flow: StarterFlow): void {
  const ids = new Set(flow.nodes.map((n) => n.id))
  for (const edge of flow.edges) {
    const missing = !ids.has(edge.from) ? edge.from : !ids.has(edge.to) ? edge.to : null
    if (missing !== null)
      throw new DanglingEdgeError(
        `La arista ${edge.from} → ${edge.to} apunta a un nodo que no existe: ${missing}`,
      )
  }
}
